<template>
  <div class="license-management">
    <div class="toolbar" v-fade-in>
      <button class="add-btn" @click="showAddModal = true">
        添加授权
      </button>
    </div>

    <!-- PC端表格 -->
    <table class="license-table desktop-only" v-motion="{ initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.4, delay: 0.05 } }">
      <thead>
        <tr>
          <th>ID</th>
          <th>授权类型</th>
          <th>域名/IP</th>
          <th>状态</th>
          <th>过期时间</th>
          <th>最后验证时间</th>
          <th>备注</th>
          <th>操作</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="license in licenses" :key="license.id">
          <td>{{ license.id }}</td>
          <td>
            <span :class="['type-tag', license.authType]">
              {{ license.authType === 'domain' ? '域名' : 'IP' }}
            </span>
          </td>
          <td>
            <span class="auth-value">{{ license.authType === 'domain' ? license.domain : license.ipAddress }}</span>
          </td>
          <td>
            <span :class="['status-tag', license.status]">{{ getStatusName(license.status) }}</span>
          </td>
          <td>
            <span v-if="license.expireTime" :class="getExpireClass(license.expireTime)">
              {{ formatDate(license.expireTime) }}
            </span>
            <span v-else class="expire-status normal">永久</span>
          </td>
          <td>
            <span v-if="license.lastVerifiedAt" class="last-verified">
              {{ formatDate(license.lastVerifiedAt) }}
            </span>
            <span v-else class="text-muted">未验证</span>
          </td>
          <td>
            <span class="remark">{{ license.remark || '-' }}</span>
          </td>
          <td>
            <button class="action-btn edit" @click="openEditModal(license)" v-if="license.status === 'active'">编辑</button>
            <button class="action-btn revoke" @click="revokeLicense(license.id)" v-if="license.status === 'active'">撤销</button>
            <button class="action-btn delete" @click="deleteLicense(license.id)">删除</button>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- 移动端卡片列表 -->
    <div class="license-cards mobile-only" v-motion="{ initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.4, delay: 0.05 } }">
      <div class="license-card" v-for="license in licenses" :key="license.id">
        <div class="card-header">
          <span :class="['type-tag', license.authType]">
            {{ license.authType === 'domain' ? '域名' : 'IP' }}
          </span>
          <span :class="['status-tag', license.status]">{{ getStatusName(license.status) }}</span>
        </div>
        <div class="card-body">
          <div class="card-row">
            <span class="card-label">{{ license.authType === 'domain' ? '域名' : 'IP地址' }}</span>
            <span class="auth-value">{{ license.authType === 'domain' ? license.domain : license.ipAddress }}</span>
          </div>
          <div class="card-row">
            <span class="card-label">过期时间</span>
            <span v-if="license.expireTime" :class="getExpireClass(license.expireTime)">
              {{ formatDate(license.expireTime) }}
            </span>
            <span v-else class="expire-status normal">永久</span>
          </div>
          <div class="card-row" v-if="license.lastVerifiedAt">
            <span class="card-label">最后验证</span>
            <span class="last-verified">{{ formatDate(license.lastVerifiedAt) }}</span>
          </div>
          <div class="card-row" v-if="license.remark">
            <span class="card-label">备注</span>
            <span class="remark">{{ license.remark }}</span>
          </div>
        </div>
        <div class="card-actions">
          <button class="action-btn edit" @click="openEditModal(license)" v-if="license.status === 'active'">编辑</button>
          <button class="action-btn revoke" @click="revokeLicense(license.id)" v-if="license.status === 'active'">撤销</button>
          <button class="action-btn delete" @click="deleteLicense(license.id)">删除</button>
        </div>
      </div>
    </div>

    <!-- 添加授权弹窗 -->
    <div class="modal-overlay" v-if="showAddModal" @click.self="showAddModal = false">
      <div class="modal">
        <h3>添加授权</h3>
        <div class="form-group">
          <label>授权类型</label>
          <select v-model="newLicense.authType">
            <option value="domain">域名</option>
            <option value="ip">IP地址</option>
          </select>
        </div>
        <div class="form-group" v-if="newLicense.authType === 'domain'">
          <label>域名</label>
          <input v-model="newLicense.domain" type="text" placeholder="例如: example.com">
        </div>
        <div class="form-group" v-if="newLicense.authType === 'ip'">
          <label>IP地址</label>
          <input v-model="newLicense.ipAddress" type="text" placeholder="例如: 192.168.1.1">
        </div>
        <div class="form-group">
          <label>过期时间（可选）</label>
          <input v-model="newLicense.expireTime" type="datetime-local" placeholder="留空表示永久">
        </div>
        <div class="form-group">
          <label>备注</label>
          <input v-model="newLicense.remark" type="text" placeholder="请输入备注信息">
        </div>
        <div class="modal-actions">
          <button @click="showAddModal = false">取消</button>
          <button class="primary" @click="addLicense">确定</button>
        </div>
      </div>
    </div>

    <!-- 编辑授权弹窗 -->
    <div class="modal-overlay" v-if="showEditModal" @click.self="showEditModal = false">
      <div class="modal">
        <h3>编辑授权</h3>
        <div class="form-group">
          <label>授权类型</label>
          <input type="text" :value="editLicense.authType === 'domain' ? '域名' : 'IP地址'" disabled>
        </div>
        <div class="form-group" v-if="editLicense.authType === 'domain'">
          <label>域名</label>
          <input v-model="editLicense.domain" type="text" placeholder="例如: example.com">
        </div>
        <div class="form-group" v-if="editLicense.authType === 'ip'">
          <label>IP地址</label>
          <input v-model="editLicense.ipAddress" type="text" placeholder="例如: 192.168.1.1">
        </div>
        <div class="form-group">
          <label>过期时间（可选）</label>
          <input v-model="editLicense.expireTime" type="datetime-local" placeholder="留空表示永久">
        </div>
        <div class="form-group">
          <label>备注</label>
          <input v-model="editLicense.remark" type="text" placeholder="请输入备注信息">
        </div>
        <div class="modal-actions">
          <button @click="showEditModal = false">取消</button>
          <button class="primary" @click="updateLicense">确定</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiFetch } from '@/config/api'

export default {
  name: 'LicenseManagement',
  data() {
    return {
      licenses: [],
      showAddModal: false,
      showEditModal: false,
      newLicense: {
        authType: 'domain',
        domain: '',
        ipAddress: '',
        expireTime: '',
        remark: ''
      },
      editLicense: {
        id: null,
        authType: '',
        domain: '',
        ipAddress: '',
        expireTime: '',
        remark: ''
      }
    }
  },
  mounted() {
    this.loadLicenses()
  },
  methods: {
    async loadLicenses() {
      try {
        const response = await apiFetch('/api/license/list', {
          method: 'GET'
        })
        if (response.code === 0) {
          this.licenses = response.data
        } else {
          alert('加载授权列表失败：' + response.msg)
        }
      } catch (error) {
        console.error('加载授权列表失败:', error)
        alert('加载授权列表失败：' + error.message)
      }
    },
    async addLicense() {
      if (this.newLicense.authType === 'domain' && !this.newLicense.domain) {
        alert('请输入域名')
        return
      }
      if (this.newLicense.authType === 'ip' && !this.newLicense.ipAddress) {
        alert('请输入IP地址')
        return
      }

      try {
        const requestData = {
          authType: this.newLicense.authType,
          domain: this.newLicense.authType === 'domain' ? this.newLicense.domain : null,
          ipAddress: this.newLicense.authType === 'ip' ? this.newLicense.ipAddress : null,
          expireTime: this.newLicense.expireTime || null,
          remark: this.newLicense.remark
        }

        const response = await apiFetch('/api/license/add', {
          method: 'POST',
          body: JSON.stringify(requestData)
        })

        if (response.code === 0) {
          alert('添加授权成功')
          this.showAddModal = false
          this.newLicense = {
            authType: 'domain',
            domain: '',
            ipAddress: '',
            expireTime: '',
            remark: ''
          }
          this.loadLicenses()
        } else {
          alert('添加授权失败：' + response.msg)
        }
      } catch (error) {
        console.error('添加授权失败:', error)
        alert('添加授权失败：' + error.message)
      }
    },
    openEditModal(license) {
      this.editLicense = {
        id: license.id,
        authType: license.authType,
        domain: license.domain || '',
        ipAddress: license.ipAddress || '',
        expireTime: license.expireTime ? this.formatDateTimeLocal(license.expireTime) : '',
        remark: license.remark || ''
      }
      this.showEditModal = true
    },
    async updateLicense() {
      if (this.editLicense.authType === 'domain' && !this.editLicense.domain) {
        alert('请输入域名')
        return
      }
      if (this.editLicense.authType === 'ip' && !this.editLicense.ipAddress) {
        alert('请输入IP地址')
        return
      }

      try {
        const requestData = {
          authType: this.editLicense.authType,
          domain: this.editLicense.authType === 'domain' ? this.editLicense.domain : null,
          ipAddress: this.editLicense.authType === 'ip' ? this.editLicense.ipAddress : null,
          expireTime: this.editLicense.expireTime || null,
          remark: this.editLicense.remark
        }

        const response = await apiFetch(`/api/license/update/${this.editLicense.id}`, {
          method: 'PUT',
          body: JSON.stringify(requestData)
        })

        if (response.code === 0) {
          alert('更新授权成功')
          this.showEditModal = false
          this.loadLicenses()
        } else {
          alert('更新授权失败：' + response.msg)
        }
      } catch (error) {
        console.error('更新授权失败:', error)
        alert('更新授权失败：' + error.message)
      }
    },
    async revokeLicense(id) {
      if (!confirm('确定要撤销此授权吗？')) {
        return
      }

      try {
        const response = await apiFetch(`/api/license/revoke/${id}`, {
          method: 'PUT'
        })

        if (response.code === 0) {
          alert('撤销授权成功')
          this.loadLicenses()
        } else {
          alert('撤销授权失败：' + response.msg)
        }
      } catch (error) {
        console.error('撤销授权失败:', error)
        alert('撤销授权失败：' + error.message)
      }
    },
    async deleteLicense(id) {
      if (!confirm('确定要删除此授权吗？此操作不可恢复！')) {
        return
      }

      try {
        const response = await apiFetch(`/api/license/delete/${id}`, {
          method: 'DELETE'
        })

        if (response.code === 0) {
          alert('删除授权成功')
          this.loadLicenses()
        } else {
          alert('删除授权失败：' + response.msg)
        }
      } catch (error) {
        console.error('删除授权失败:', error)
        alert('删除授权失败：' + error.message)
      }
    },
    getStatusName(status) {
      const statusMap = {
        'active': '激活',
        'expired': '已过期',
        'revoked': '已撤销'
      }
      return statusMap[status] || status
    },
    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    formatDateTimeLocal(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day}T${hours}:${minutes}`
    },
    getExpireClass(expireTime) {
      if (!expireTime) return 'expire-status normal'
      const now = new Date()
      const expire = new Date(expireTime)
      const daysLeft = Math.ceil((expire - now) / (1000 * 60 * 60 * 24))

      if (daysLeft <= 0) return 'expire-status expired'
      if (daysLeft <= 3) return 'expire-status critical'
      if (daysLeft <= 7) return 'expire-status warning'
      return 'expire-status normal'
    }
  }
}
</script>

<style scoped>
/* 简约风格 - 授权管理 */
.license-management {
  padding: 20px;
}

.toolbar {
  margin-bottom: 20px;
  display: flex;
  gap: 10px;
}

.add-btn {
  padding: 10px 20px;
  background: #000;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
}

.add-btn:hover {
  background: #333;
}

/* PC端表格样式 - 简约风格 */
.license-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 0;
  overflow: hidden;
  border: 1px solid #e0e0e0;
}

.license-table thead {
  background: #fafafa;
  color: #333;
  border-bottom: 2px solid #e0e0e0;
}

.license-table th {
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.license-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  font-size: 14px;
}

.license-table tbody tr:hover {
  background: #fafafa;
}

.type-tag {
  padding: 3px 10px;
  border-radius: 2px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid;
}

.type-tag.domain {
  background: white;
  color: #1976d2;
  border-color: #1976d2;
}

.type-tag.ip {
  background: white;
  color: #7b1fa2;
  border-color: #7b1fa2;
}

.status-tag {
  padding: 3px 10px;
  border-radius: 2px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid;
}

.status-tag.active {
  background: white;
  color: #2e7d32;
  border-color: #2e7d32;
}

.status-tag.expired {
  background: white;
  color: #c62828;
  border-color: #c62828;
}

.status-tag.revoked {
  background: white;
  color: #757575;
  border-color: #757575;
}

.auth-value {
  font-family: monospace;
  color: #333;
  font-weight: 500;
}

.expire-status {
  padding: 3px 8px;
  border-radius: 2px;
  font-size: 12px;
  border: 1px solid;
}

.expire-status.normal {
  background: white;
  color: #2e7d32;
  border-color: #2e7d32;
}

.expire-status.warning {
  background: white;
  color: #ef6c00;
  border-color: #ef6c00;
}

.expire-status.critical {
  background: white;
  color: #c62828;
  border-color: #c62828;
}

.expire-status.expired {
  background: white;
  color: #757575;
  border-color: #757575;
}

.last-verified {
  color: #666;
  font-size: 12px;
}

.text-muted {
  color: #999;
}

.remark {
  color: #666;
  font-size: 13px;
}

.action-btn {
  padding: 6px 12px;
  margin-right: 8px;
  border: 1px solid;
  border-radius: 2px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
  background: white;
}

.action-btn.edit {
  color: #1976d2;
  border-color: #1976d2;
}

.action-btn.edit:hover {
  background: #1976d2;
  color: white;
}

.action-btn.revoke {
  color: #ef6c00;
  border-color: #ef6c00;
}

.action-btn.revoke:hover {
  background: #ef6c00;
  color: white;
}

.action-btn.delete {
  color: #c62828;
  border-color: #c62828;
}

.action-btn.delete:hover {
  background: #c62828;
  color: white;
}

/* 移动端卡片样式 - 简约风格 */
.license-cards {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.license-card {
  background: white;
  border-radius: 0;
  padding: 15px;
  border: 1px solid #e0e0e0;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
}

.card-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
}

.card-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
}

.card-label {
  color: #666;
  font-weight: 500;
}

.card-actions {
  display: flex;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

.card-actions .action-btn {
  flex: 1;
  margin-right: 0;
}

/* 弹窗样式 - 简约风格 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: white;
  border-radius: 0;
  padding: 24px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
}

.modal h3 {
  margin: 0 0 20px 0;
  color: #333;
  font-weight: 600;
  font-size: 18px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  color: #666;
  font-size: 14px;
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 2px;
  font-size: 14px;
  box-sizing: border-box;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #000;
}

.form-group input:disabled {
  background: #f5f5f5;
  cursor: not-allowed;
}

.modal-actions {
  display: flex;
  gap: 12px;
  margin-top: 24px;
}

.modal-actions button {
  flex: 1;
  padding: 10px;
  border: 1px solid;
  border-radius: 2px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
  font-weight: 500;
}

.modal-actions button:first-child {
  background: white;
  color: #666;
  border-color: #ddd;
}

.modal-actions button:first-child:hover {
  background: #f5f5f5;
}

.modal-actions button:last-child {
  background: #000;
  color: white;
  border-color: #000;
}

.modal-actions button:last-child:hover {
  background: #333;
}

.modal-actions button.primary {
  background: #000;
  color: white;
  border-color: #000;
}

.modal-actions button.primary:hover {
  background: #333;
}

/* 响应式 */
.desktop-only {
  display: table;
}

.mobile-only {
  display: none;
}

@media (max-width: 768px) {
  .desktop-only {
    display: none;
  }

  .mobile-only {
    display: flex;
  }

  .license-management {
    padding: 10px;
  }

  .modal {
    width: 95%;
    padding: 20px;
  }
}
</style>
